import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, useTheme, useMediaQuery, Button, Menu, MenuItem, IconButton, Card, CardContent, Grid, Chip, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ContactBanner, SiteFooter } from './Home';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

// Interfaces TypeScript
interface ActivityItem {
  id: number;
  title: string;
  description?: string;
  image_url: string;
  alt_text?: string;
  order_index: number;
}

interface ActivitySection {
  id: number;
  name: string;
  title: string;
  description?: string;
  section_type: 'vie_academique' | 'moments_forts';
  order_index: number;
  items: ActivityItem[];
}

interface ActivityImage {
  id: number;
  activity_id: number;
  image_url: string;
  alt_text?: string;
  order_index: number;
  created_at: string;
}

// Composant de navigation avec onglets (réutilisé de Home.tsx)
const NavigationBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [scolariteMenuAnchor, setScolariteMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleScolariteMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setScolariteMenuAnchor(event.currentTarget);
  };

  const handleScolariteMenuClose = () => {
    setScolariteMenuAnchor(null);
  };

  const handleContactsClick = () => {
    const footerElement = document.getElementById('footer');
    if (footerElement) {
      footerElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigationItems = [
    { label: 'ACCUEIL', path: '/' },
    { label: 'PRESENTATION', path: '/presentation' },
    { 
      label: 'SCOLARITE', 
      path: '/scolarite',
      hasSubmenu: true,
      submenu: [
        { label: 'MATERNELLE', path: '/scolarite' },
        { label: 'CRÈCHE/GARDERIE', path: '/scolarite-maternel' }
      ]
    },
    { label: 'NOS ACTIVITES', path: '/activites' },
    { label: 'BLOG', path: '/blog' },
    { label: 'CONTACTS', path: '#footer', isScroll: true },
    { label: 'CONNEXION', path: '/login' },
  ];

  return (
    <Box 
      position="absolute" 
      sx={{ 
        bgcolor: 'transparent',
        boxShadow: 'none',
        zIndex: 10,
        top: '60px',
        width: '100%'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex',
          justifyContent: { xs: 'flex-end', md: 'space-between' },
          alignItems: 'center',
          py: { xs: 1, md: 2 },
          px: { xs: 2, md: 0 },
          minHeight: { xs: 56, md: 64 }
        }}>
          {/* Logo - Masqué sur mobile */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{
              width: 60,
              height: 60,
              border: '2px solid #87CEEB',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'white',
              overflow: 'hidden',
              p: 0.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <img
                src="/img/pages/logologo.webp"
                alt="La Maison des Enfants La petite Académie"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Box>
          </Box>

          {/* Navigation Desktop */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              gap: { md: 2, lg: 3 },
              alignItems: 'center'
            }}>
              {navigationItems.map((item) => (
                <Box key={item.label}>
                  {item.hasSubmenu ? (
                    <>
                      <Typography
                        onClick={handleScolariteMenuOpen}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: { md: 12, lg: 14 },
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          px: { md: 1.5, lg: 2 },
                          py: 1,
                          borderBottom: '2px solid transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#FF9800',
                            borderBottom: '2px solid #FF9800',
                          }
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Menu
                        anchorEl={scolariteMenuAnchor}
                        open={Boolean(scolariteMenuAnchor)}
                        onClose={handleScolariteMenuClose}
                        sx={{
                          '& .MuiPaper-root': {
                            bgcolor: 'rgba(23, 128, 194, 0.95)',
                            color: 'white',
                            mt: 1,
                            backdropFilter: 'blur(10px)',
                            minWidth: 200
                          }
                        }}
                      >
                        {item.submenu?.map((subItem) => (
                          <MenuItem
                            key={subItem.label}
                            component={RouterLink}
                            to={subItem.path}
                            onClick={handleScolariteMenuClose}
                            sx={{
                              color: 'white',
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: 'uppercase',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                              }
                            }}
                          >
                            {subItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : item.isScroll ? (
                    <Typography
                      onClick={handleContactsClick}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: '2px solid transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  ) : (
                    <Typography
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: item.label === 'NOS ACTIVITES' ? '#FF9800' : 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: item.label === 'NOS ACTIVITES' ? '2px solid #FF9800' : '2px solid transparent',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Menu Mobile */}
          {isMobile && (
            <>
              <IconButton
                color="inherit"
                onClick={handleMobileMenuOpen}
                sx={{ color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    bgcolor: 'rgba(23, 128, 194, 0.95)',
                    color: 'white',
                    mt: 1,
                    backdropFilter: 'blur(10px)'
                  }
                }}
              >
                {navigationItems.map((item) => (
                  <Box key={item.label}>
                    {item.hasSubmenu ? (
                      <>
                        <MenuItem
                          onClick={handleScolariteMenuOpen}
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: 14,
                            textTransform: 'uppercase',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          {item.label}
                          <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: 16 }} />
                        </MenuItem>
                        <Menu
                          anchorEl={scolariteMenuAnchor}
                          open={Boolean(scolariteMenuAnchor)}
                          onClose={handleScolariteMenuClose}
                          sx={{
                            '& .MuiPaper-root': {
                              bgcolor: 'rgba(23, 128, 194, 0.95)',
                              color: 'white',
                              mt: 1,
                              backdropFilter: 'blur(10px)',
                              minWidth: 200
                            }
                          }}
                        >
                          {item.submenu?.map((subItem) => (
                            <MenuItem
                              key={subItem.label}
                              component={RouterLink}
                              to={subItem.path}
                              onClick={() => {
                                handleScolariteMenuClose();
                                handleMobileMenuClose();
                              }}
                              sx={{
                                color: 'white',
                                fontWeight: 600,
                                fontSize: 14,
                                textTransform: 'uppercase',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                                }
                              }}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : item.isScroll ? (
                      <MenuItem
                        onClick={() => {
                          handleContactsClick();
                          handleMobileMenuClose();
                        }}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        component={RouterLink}
                        to={item.path}
                        onClick={handleMobileMenuClose}
                        sx={{
                          color: item.label === 'NOS ACTIVITES' ? '#FF9800' : 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    )}
                  </Box>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

// Section Hero pour l'activité
const ActivityDetailHeroSection = ({ activity }: { activity: ActivityItem }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getImageUrl = (imageUrl: string) => {
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    if (imageUrl.startsWith('/img/pages/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    if (imageUrl.startsWith('/uploads/pages/')) {
      return `https://lapetiteacademie.ci/api/dynamic-pages/images/${imageUrl.split('/').pop()}`;
    }
    
    if (imageUrl.startsWith('/uploads/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    return `https://lapetiteacademie.ci${imageUrl}`;
  };

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '50vh',
      backgroundImage: `url(${getImageUrl(activity.image_url)})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }}>
      {/* Overlay sombre semi-transparent */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 1
      }} />

      {/* Contenu principal */}
      <Box sx={{
        position: 'relative',
        zIndex: 3,
        textAlign: 'center',
        color: 'white',
        maxWidth: '80%'
      }}>
        <Typography
          variant="h1"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: { xs: 24, md: 36, lg: 48 },
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            mb: 2,
            fontFamily: 'Arial, sans-serif',
            lineHeight: 1.2
          }}
        >
          {activity.title}
        </Typography>
        
        {activity.description && (
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 400,
              fontSize: { xs: 16, md: 20 },
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              fontFamily: 'Arial, sans-serif',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            {activity.description}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Galerie d'images pour l'activité
const ActivityGallerySection = ({ activity, images }: { activity: ActivityItem; images: ActivityImage[] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const getImageUrl = (imageUrl: string) => {
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    if (imageUrl.startsWith('/img/pages/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    if (imageUrl.startsWith('/uploads/pages/')) {
      return `https://lapetiteacademie.ci/api/dynamic-pages/images/${imageUrl.split('/').pop()}`;
    }
    
    if (imageUrl.startsWith('/uploads/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    return `https://lapetiteacademie.ci${imageUrl}`;
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(getImageUrl(imageUrl));
  };

  const handleCloseDialog = () => {
    setSelectedImage(null);
  };

  // Combiner l'image principale avec les images supplémentaires
  const allImages = [
    { id: 0, image_url: activity.image_url, alt_text: activity.alt_text || activity.title },
    ...images
  ];

  return (
    <Box sx={{
      bgcolor: 'white',
      py: { xs: 4, md: 6 },
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Motifs décoratifs en arrière-plan */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/img/pages/doodles.png)',
        backgroundSize: '200px',
        backgroundRepeat: 'repeat',
        opacity: 0.1,
        zIndex: 0
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Titre de la galerie */}
        <Box sx={{
          textAlign: 'center',
          mb: 4
        }}>
          <Typography
            variant="h3"
            sx={{
              color: '#1780c2',
              fontWeight: 700,
              fontSize: { xs: 24, md: 32 },
              mb: 2,
              fontFamily: 'Arial, sans-serif'
            }}
          >
            Galerie Photos
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              fontWeight: 400,
              fontSize: { xs: 14, md: 16 },
              fontFamily: 'Arial, sans-serif'
            }}
          >
            Découvrez tous les moments de cette activité
          </Typography>
        </Box>

        {/* Grille des images */}
        <Grid container spacing={2}>
          {allImages.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} key={image.id || index}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, boxShadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                  }
                }}
                onClick={() => handleImageClick(image.image_url)}
              >
                <Box sx={{
                  height: { xs: 200, sm: 250, md: 300 },
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <img
                    src={getImageUrl(image.image_url)}
                    alt={image.alt_text || activity.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    onError={(e) => {
                      e.currentTarget.src = `https://picsum.photos/400/300?random=${image.id || index}`;
                    }}
                  />
                  
                  {/* Overlay au survol */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    '&:hover': {
                      opacity: 1
                    }
                  }}>
                    <PhotoLibraryIcon sx={{ color: 'white', fontSize: 40 }} />
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Message si pas d'images supplémentaires */}
        {images.length === 0 && (
          <Box sx={{
            textAlign: 'center',
            py: 4
          }}>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                fontStyle: 'italic'
              }}
            >
              Aucune photo supplémentaire disponible pour cette activité.
            </Typography>
          </Box>
        )}
      </Container>

      {/* Dialog pour afficher l'image en grand */}
      <Dialog
        open={!!selectedImage}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'transparent',
            boxShadow: 'none'
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {selectedImage && (
            <>
              <IconButton
                onClick={handleCloseDialog}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  zIndex: 1,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.7)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
              <img
                src={selectedImage}
                alt="Image en grand"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Composant principal ActivityDetail
const ActivityDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activity, setActivity] = useState<ActivityItem | null>(null);
  const [images, setImages] = useState<ActivityImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadActivity = async () => {
      try {
        setLoading(true);
        
        // Charger l'activité et ses images en parallèle
        const [activityResponse, imagesResponse] = await Promise.all([
          axios.get(`https://lapetiteacademie.ci/api/dynamic-pages/activities/${id}`),
          axios.get(`https://lapetiteacademie.ci/api/dynamic-pages/activities/${id}/images`)
        ]);
        
        if (activityResponse.data.status === 'success') {
          setActivity(activityResponse.data.data);
        }
        
        if (imagesResponse.data.status === 'success') {
          setImages(imagesResponse.data.data || []);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'activité:', error);
        setError('Erreur lors du chargement de l\'activité');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadActivity();
    }
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  if (error || !activity) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        gap: 2
      }}>
        <Typography variant="h5" color="error">
          {error || 'Activité non trouvée'}
        </Typography>
        <Button
          component={RouterLink}
          to="/activites"
          startIcon={<ArrowBackIcon />}
          variant="contained"
          sx={{
            bgcolor: '#1780c2',
            '&:hover': { bgcolor: '#FF9800' }
          }}
        >
          Retour aux activités
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{activity.title} - La Petite Académie Abidjan</title>
        <meta name="description" content={activity.description || `Découvrez ${activity.title} à La Petite Académie à Abidjan.`} />
        <meta name="keywords" content="activités éducatives Abidjan, La Petite Académie, crèche maternelle bilingue, activités enfants" />
        <meta property="og:title" content={`${activity.title} - La Petite Académie Abidjan`} />
        <meta property="og:description" content={activity.description || `Découvrez ${activity.title} à La Petite Académie à Abidjan.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://lapetiteacademie.ci/activites/${activity.id}`} />
        <link rel="canonical" href={`https://lapetiteacademie.ci/activites/${activity.id}`} />
      </Helmet>
      <Box sx={{ 
        width: '100%',
        margin: 0,
        padding: 0
      }}>
        <ContactBanner />
        <NavigationBar />
        <ActivityDetailHeroSection activity={activity} />
        <ActivityGallerySection activity={activity} images={images} />
        
        {/* Bouton retour */}
        <Box sx={{
          bgcolor: '#f8f9fa',
          py: 4,
          textAlign: 'center'
        }}>
          <Button
            component={RouterLink}
            to="/activites"
            startIcon={<ArrowBackIcon />}
            variant="contained"
            size="large"
            sx={{
              bgcolor: '#1780c2',
              px: 4,
              py: 1.5,
              fontSize: 16,
              fontWeight: 600,
              '&:hover': { 
                bgcolor: '#FF9800',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Retour aux activités
          </Button>
        </Box>
        
        <SiteFooter />
      </Box>
    </>
  );
};

export default ActivityDetail;
